/**
 * Servicio para gestión de sincronización offline
 * Permite encolar peticiones que fallan por falta de red o porque el servidor está dormido
 */

const QUEUE_KEY = 'posent_offline_queue';

class SyncService {
    constructor() {
        this.queue = this.loadQueue();
        this.isSyncing = false;
        
        // Iniciar el proceso de sincronización automática si hay internet
        window.addEventListener('online', () => {
            console.log('App online, intentando sincronizar...');
            this.sync();
        });

        // Intentar sincronizar cada 2 minutos en caso de que el online event no se dispare
        setInterval(() => {
            if (navigator.onLine && this.queue.length > 0) {
                this.sync();
            }
        }, 120000);
    }

    loadQueue() {
        try {
            const saved = localStorage.getItem(QUEUE_KEY);
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            console.error('Error loading sync queue:', e);
            return [];
        }
    }

    saveQueue() {
        try {
            localStorage.setItem(QUEUE_KEY, JSON.stringify(this.queue));
        } catch (e) {
            console.error('Error saving sync queue:', e);
        }
    }

    /**
     * Encolar una petición para sincronización posterior
     * @param {string} url - El endpoint
     * @param {string} method - POST, PUT, DELETE
     * @param {Object} data - Los datos de la petición
     * @param {string} actionName - Nombre descriptivo para mostrar al usuario
     */
    enqueue(url, method, data, actionName = 'Operación') {
        const item = {
            id: Date.now() + Math.random().toString(36).substr(2, 9),
            url,
            method,
            data,
            actionName,
            timestamp: new Date().toISOString(),
            retryCount: 0
        };

        this.queue.push(item);
        this.saveQueue();
        console.log(`Petición encolada: ${actionName}`, item);
        
        // Intentar notificar al usuario (podríamos usar un evento global o un store)
        window.dispatchEvent(new CustomEvent('offline-action-queued', { detail: item }));
        
        return item;
    }

    /**
     * Intentar sincronizar toda la cola
     */
    async sync() {
        if (this.isSyncing || this.queue.length === 0 || !navigator.onLine) return;

        this.isSyncing = true;
        console.log(`Iniciando sincronización de ${this.queue.length} elementos...`);

        const remainingQueue = [];
        const api = (await import('../config/api')).default;

        for (const item of this.queue) {
            try {
                console.log(`Sincronizando: ${item.actionName}...`);
                await api({
                    method: item.method,
                    url: item.url,
                    data: item.data
                });
                console.log(`Sincronizado con éxito: ${item.actionName}`);
                window.dispatchEvent(new CustomEvent('offline-action-synced', { detail: item }));
            } catch (error) {
                console.error(`Fallo al sincronizar ${item.actionName}:`, error);
                item.retryCount++;
                // Si ha fallado muchas veces, tal vez algo está mal con los datos, pero lo mantenemos por ahora
                remainingQueue.push(item);
            }
        }

        this.queue = remainingQueue;
        this.saveQueue();
        this.isSyncing = false;
        
        if (this.queue.length === 0) {
            console.log('Todos los elementos sincronizados.');
            window.dispatchEvent(new CustomEvent('sync-completed'));
        }
    }

    getQueue() {
        return this.queue;
    }

    clearQueue() {
        this.queue = [];
        this.saveQueue();
    }
}

const syncService = new SyncService();
export default syncService;
